'use client';
import React, { FC, useState } from 'react';
import { Button, GenInternalLink, GenNavTile } from '@/components';
import { TypeAccountNavTileFields } from '@/core/generated-types';
import {
  TypeUserPreferenceRecord,
  TypeUserRecord,
} from '@/lib/api/accountManager/types';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations';
import { editUser } from '@/lib/api/accountManager/apis';
import Input from 'react-phone-number-input/input';
import {
  CountryCode,
  E164Number,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.min.json';
import 'react-phone-number-input/style.css';
import { Alert } from '@/components/ui/Alert';

export type CommunicationPreferencesTileProps = {
  t: TypeAccountSettingsPageTranslations;
  user: TypeUserRecord;
  tile: TypeAccountNavTileFields;
  locale: string | undefined;
};

const _getCountryCodeFromLocale = (locale: string | undefined): CountryCode => {
  if (!locale) {
    return 'US'; // Default to 'US' if locale is not provided
  }

  const localeParts = locale.split('-');
  const country =
    localeParts.length > 1
      ? localeParts[1].toUpperCase()
      : localeParts[0].toUpperCase();

  const countryCodes = metadata.countries as Record<string, any>;
  if (country in countryCodes) {
    return country as CountryCode;
  }

  return 'US'; // Fallback to 'US' if country is not found
};

const _getParsedNumber = (phoneNumber: string, locale: string): string => {
  const parsedNumber = parsePhoneNumberFromString(phoneNumber);

  if (parsedNumber) {
    return locale === 'US'
      ? parsedNumber?.formatNational()
      : parsedNumber?.formatInternational();
  } else {
    return '';
  }
};

const CommunicationPreferencesTile: FC<CommunicationPreferencesTileProps> = ({
  t,
  user,
  tile,
  locale,
}) => {
  const { title, subheader, description, subfooter } = tile;
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>(
    user.phone_number || '',
  );
  const [phoneNumberValidationError, setPhoneNumberValidationError] =
    useState<string>('');
  const [isServerError, setIsServerError] = useState(false);
  const editCommunicationPreferencesUrl = `/account/settings/edit-communication-preferences`;
  const countryCode = _getCountryCodeFromLocale(locale);
  const isUserPhoneNumberPresent = Boolean(user.phone_number?.trim());
  const parsedNumber = _getParsedNumber(phoneNumber, countryCode);

  const clearServerError = () => {
    if (isServerError) {
      setIsServerError(false);
    }
  };

  const validatePhoneNumber = (value: string) => {
    const parsedPhoneNumber = parsePhoneNumberFromString(value, countryCode);
    if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
      setPhoneNumberValidationError('');
    } else {
      setPhoneNumberValidationError(t.invalidPhoneNumberMessage);
    }
  };

  const updatePhoneNumber = (value: E164Number | undefined) => {
    const trimmedValue = value ? value.trim() : '';
    setPhoneNumber(trimmedValue);
    validatePhoneNumber(trimmedValue);
    clearServerError();
  };

  const handleSubmit = async () => {
    setIsProcessing(true);
    setIsSuccess(true);
    try {
      const response = await editUser({
        userUuid: user.uuid,
        userData: {
          phone_number: phoneNumber,
          user_preferences: [
            {
              preference_name: 'marketing_sms',
              preference_value: true,
            },
            {
              preference_name: 'transactional_sms',
              preference_value: true,
            },
          ] as TypeUserPreferenceRecord[],
        },
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setIsServerError(true);
      }
    } catch (error) {
      setIsServerError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <GenNavTile
      title={title}
      titleDecorator={
        isUserPhoneNumberPresent && (
          <GenInternalLink
            href={editCommunicationPreferencesUrl}
            cta={t.editCta}
          />
        )
      }
    >
      {isUserPhoneNumberPresent ? (
        <>
          <p className="mb-6">{description}</p>
          <div className="flex flex-col lg:flex-row space-x-1">
            <strong>{t.phoneNumberLabel}</strong>
            <span>{parsedNumber}</span>
          </div>
        </>
      ) : (
        <div className="bg-indigo-pastel p-6">
          <h4 className="text-h3 font-bold">{t.smsNotificationsLabel}</h4>
          <p className="text-base pb-6">{subheader}</p>
          {isSuccess ? (
            <Alert message={t.smsSuccessMessage} />
          ) : (
            <>
              <div className="pb-6">
                <Input
                  value={phoneNumber}
                  onChange={updatePhoneNumber}
                  placeholder={t.phonePlaceholderLabel}
                  country={countryCode}
                  className={`w-full border border-grey-midtone px-4 py-3 rounded-sm ${phoneNumberValidationError ? 'border-warning' : ''}`}
                />
                {phoneNumberValidationError && (
                  <p className="text-warning mt-1 text-sm">
                    {phoneNumberValidationError}
                  </p>
                )}
              </div>
              <Button
                type="submit"
                ctaText={t.addMyNumberCta}
                onClick={handleSubmit}
                disabled={!!phoneNumberValidationError || isProcessing}
                includeSpinner={isProcessing}
                fullWidth
              />
              {subfooter && (
                <p className="text-sm text-grey-light mt-4">{subfooter}</p>
              )}
            </>
          )}
        </div>
      )}
    </GenNavTile>
  );
};

export default React.memo(CommunicationPreferencesTile);
